import React, { Fragment, useMemo, useState } from 'react';
import {
    Box,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Theme,
    Typography,
    useTheme,
} from '@material-ui/core';
import { X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import {
    GALLERY_MEDIA_CATEGORIES,
    type TConversationMediaItem,
    type TGalleryMediaCategory,
} from 'src/views/OmniChannel/utils/conversationMediaGallery';
import {
    toMultimediaPreviewFromMessage,
    type TMultimediaPreview,
} from 'src/views/OmniChannel/utils/outboundMultimedia';
import { useConversationMediaGallery } from 'src/views/OmniChannel/hooks/useConversationMediaGallery';
import type { TMediaGalleryTab } from 'src/views/OmniChannel/constants/mediaGallery';
import { OutboundMultimediaPreviewModal } from '../multimedia';
import { MediaGalleryItem } from './MediaGalleryItem';
import { MediaGallerySkeleton } from './MediaGallerySkeleton';

const CATEGORY_LABEL_KEYS: Record<TGalleryMediaCategory, string> = {
    images: 'Chat.MediaGalleryImages',
    videos: 'Chat.MediaGalleryVideos',
    audios: 'Chat.MediaGalleryAudios',
    documents: 'Chat.MediaGalleryDocuments',
};

const MediaGallerySection = ({
    title,
    items,
    onOpen,
}: {
    title: string;
    items: TConversationMediaItem[];
    onOpen: (item: TConversationMediaItem) => void;
}) => {
    if (!items.length) return null;

    const gridItems = items.filter((item) =>
        ['images', 'videos'].includes(item.mediaType)
    );
    const listItems = items.filter((item) =>
        ['audios', 'documents'].includes(item.mediaType)
    );

    return (
        <Box mb={3}>
            <Typography
                variant="subtitle1"
                color="textPrimary"
                style={{ marginBottom: 12, fontWeight: 600 }}
            >
                {title} ({items.length})
            </Typography>

            {gridItems.length > 0 && (
                <Grid container spacing={2} style={{ marginBottom: 12 }}>
                    {gridItems.map((item) => (
                        <Grid item xs={6} sm={4} md={3} key={item.id}>
                            <MediaGalleryItem
                                item={item}
                                variant="grid"
                                onOpen={onOpen}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {listItems.length > 0 && (
                <Box display="flex" flexDirection="column" gridGap={8}>
                    {listItems.map((item) => (
                        <MediaGalleryItem
                            key={item.id}
                            item={item}
                            variant="list"
                            onOpen={onOpen}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export const MediaGallery = ({
    conversationId,
    onClose,
}: {
    conversationId: string;
    onClose: () => void;
}) => {
    const theme = useTheme<{
        palette: Theme['palette'] & { background: { dark: string } };
    }>();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<TMediaGalleryTab>('all');
    const [preview, setPreview] = useState<TMultimediaPreview | null>(null);

    const { groupedMedia, galleryLoading } =
        useConversationMediaGallery({
            conversationId,
            tab: activeTab,
            enabled: Boolean(conversationId),
        });

    const activeItems =
        activeTab === 'all' ? groupedMedia.all : groupedMedia[activeTab];

    const handleOpenItem = (item: TConversationMediaItem) => {
        setPreview(
            toMultimediaPreviewFromMessage({
                id: item.id,
                mediaUrl: item.source,
                mediaContentType: item.contentType,
                name: item.name,
            })
        );
    };

    const tabs: TMediaGalleryTab[] = useMemo(
        () => ['all', ...GALLERY_MEDIA_CATEGORIES],
        []
    );

    return (
        <Fragment>
            <Box
                p={2}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.dark,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 8,
                    }}
                >
                    <IconButton color="primary" onClick={onClose}>
                        <X />
                    </IconButton>
                    <Typography variant="h5" color="textPrimary">
                        {t('Chat.MediaGalleryTitle')}
                    </Typography>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={(_, value: TMediaGalleryTab) => setActiveTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    {tabs.map((tab) => (
                        <Tab
                            key={tab}
                            value={tab}
                            label={
                                tab === 'all'
                                    ? t('Chat.MediaGalleryAll')
                                    : t(CATEGORY_LABEL_KEYS[tab])
                            }
                        />
                    ))}
                </Tabs>
            </Box>

            <Box
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px 25px',
                    backgroundColor: theme.palette.background.dark,
                }}
            >
                {galleryLoading ? (
                    <MediaGallerySkeleton
                        variant={
                            activeTab === 'all' ||
                            ['images', 'videos'].includes(activeTab)
                                ? 'grid'
                                : 'list'
                        }
                    />
                ) : activeTab === 'all' ? (
                    !groupedMedia.all.length ? (
                        <Box style={{ padding: 40, textAlign: 'center' }}>
                            <Typography variant="body1" color="textSecondary">
                                {t('Chat.MediaGalleryEmpty')}
                            </Typography>
                        </Box>
                    ) : (
                        GALLERY_MEDIA_CATEGORIES.map((category) => (
                            <MediaGallerySection
                                key={category}
                                title={t(CATEGORY_LABEL_KEYS[category])}
                                items={groupedMedia[category]}
                                onOpen={handleOpenItem}
                            />
                        ))
                    )
                ) : !activeItems.length ? (
                    <Typography variant="body1" color="textSecondary">
                        {t('Chat.MediaGalleryEmptyCategory')}
                    </Typography>
                ) : (
                    <MediaGallerySection
                        title={t(CATEGORY_LABEL_KEYS[activeTab])}
                        items={activeItems}
                        onOpen={handleOpenItem}
                    />
                )}
            </Box>

            <OutboundMultimediaPreviewModal
                open={Boolean(preview)}
                setOpen={(isOpen) => {
                    if (!isOpen) setPreview(null);
                }}
                preview={preview}
            />
        </Fragment>
    );
};
